import type {Options as KyOptions} from 'ky'
import ky from 'ky'
import React from 'react'

import {usePageInfo} from '@helios/shared/providers/page-info'

/**
 * All of the currently supported services. These names must exactly match the
 * names set in the backend (Lambda & Terraform)
 */
type Service =
    | 'return-on-investment-calculator'
    | 'expense-policy-builder'
    | 'expense-report-generator'

/**
 * This function is used to generate the PDF. You must provide a service name,
 * and you can send whatever data you want in the payload. The return value is a
 * string URL pointing to where the PDF is stored.
 */
export const generatePDF = async (service: Service, payload: object) => {
    interface Response {
        pdfUploadOutput: {
            Location: string
            Key: string
        }
    }

    const previewUrlParams = new URLSearchParams({
        preview: 'true',
        'hide-cookie-banner': 'true',
    })
    const previewUrl = `${window.location.href}?${previewUrlParams}`
    const endpoint = `${process.env.GATSBY_RENDER_URL_TO_PDF_API_URL}/save-and-render`
    const endpointParams = new URLSearchParams({
        preview_url: previewUrl,
    })
    const url = `${endpoint}?${endpointParams}`
    const options: KyOptions = {
        json: payload,
        headers: {
            'x-api-key': process.env.GATSBY_RENDER_URL_TO_PDF_API_KEY!,
            'x-service-name': service,
        },
        timeout: 60000,
    }

    const response = await ky.post(url, options).json<Response>()

    return response.pdfUploadOutput.Location
}

/**
 * Call this function to retrieve values stored in the backend that are linked
 * to the provided user ID. Normally called when Puppeteer visits the PDF
 * preview on the frontend while generating the PDF
 */
export const getPDFValues = async <Values extends object>(service: Service, userID: string) => {
    const endpoint = `${process.env.GATSBY_RENDER_URL_TO_PDF_API_URL}/save-and-render`
    const endpointParams = new URLSearchParams({
        'user-id': userID,
    })
    const url = `${endpoint}?${endpointParams}`
    const options: KyOptions = {
        headers: {
            'x-api-key': process.env.GATSBY_RENDER_URL_TO_PDF_API_KEY!,
            'x-service-name': service,
        },
        timeout: 60000,
    }

    const values = await ky.get(url, options).json<Values>()

    return values
}

/**
 * A hook which encapsulates all of the logic required to fetch values from the
 * backend when Puppeteer visits the PDF preview on the frontend while
 * generating the PDF. Returns the values set in state as well as a boolean
 * indicating whether or not we're in preview mode
 */
export const usePDFValues = <Values extends object>(service: Service, initialValues: Values) => {
    const [previewValues, setPreviewValues] = React.useState(initialValues)
    const {location} = usePageInfo()

    const params = new URLSearchParams(location.search)
    const isPreviewMode = params.get('preview') === 'true'
    const userID = params.get('user-id')

    React.useEffect(() => {
        if (!isPreviewMode || !userID) {
            return
        }

        const getAndSetValues = async () => {
            const values = await getPDFValues<Values>(service, userID)

            setPreviewValues(values)
        }

        try {
            getAndSetValues()
        } catch (error) {
            console.error(error)
        }
    }, [isPreviewMode, service, userID])

    return {previewValues, isPreviewMode}
}
